<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- 🔥 BADGE DE DÉTECTION DU TYPE -->
            <div v-if="detectedType && showDetectedType" class="detection-badge-container ppv2-mb-4">
                <div
                    class="detection-badge ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-px-3 ppv2-py-1 ppv2-rounded-lg ppv2-border ppv2-mb-7"
                    :class="[
                        hasDetectionAmbiguity
                            ? 'ppv2-bg-orange-50 ppv2-border-orange-400'
                            : 'ppv2-bg-[#31d0aa] ppv2-bg-opacity-10 ppv2-border-[#31d0aa]',
                        'dark:ppv2-bg-opacity-10',
                    ]"
                >
                    <span class="ppv2-text-xl" :class="hasDetectionAmbiguity ? 'ppv2-text-orange-600' : 'ppv2-text-[#31d0aa]'">
                        {{ hasDetectionAmbiguity ? '⚠️' : '✓' }}
                    </span>
                    <div class="ppv2-flex-1">
                        <div
                            class="poppinsMedium ppv2-text-sm"
                            :class="hasDetectionAmbiguity ? 'ppv2-text-orange-800' : 'ppv2-text-[#31d0aa]'"
                        >
                            {{ detectionMessage }}
                        </div>
                        <div v-if="hasDetectionAmbiguity" class="ppv2-text-xs ppv2-text-orange-600 ppv2-mt-1">
                            {{ $t('experience.step3.detection.ambiguityHint') }}
                        </div>
                    </div>
                    <div class="ppv2-text-xl poppinsBold ppv2-text-black ppv2-text-opacity-70">
                        {{ Math.round(detectionInfo.score * 100) }}%
                    </div>
                </div>
            </div>

            <DashedCard
                :title="$t('experience.step7.ticketingOptions.title')"
                :title-size="15"
                custom-class=""
                custom-class-title=""
                :marginTop="20"
                :marginBottom="0"
                min-height="115px"
                :custom-style="{ padding: normalizeSize(24, true) }"
            >
                <template #content>
                    <div
                        class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-start"
                        :style="{ gap: normalizeSize(16, true) }"
                    >
                        <BaseSwitch
                            v-if="!isSimpleTicketCircuit"
                            v-model="localVoucher"
                            :label="$t('experience.step7.ticketingOptions.description_switch_1')"
                        />
                        <div v-if="!isConfirmationFile" class="ppv2-relative">
                            <div class="ppv2-bg-white dark:ppv2-bg-dark">
                                <BaseSwitch
                                    v-model="isConfirmationFile"
                                    :label="$t('experience.step7.ticketingOptions.description_switch_2')"
                                />
                            </div>
                        </div>
                        <div class="ppv2-w-full" v-else>
                            <DashedCard
                                :marginTop="20"
                                :marginBottom="0"
                                customClassTitle="ppv2-left-1"
                                :custom-style="{ padding: normalizeSize(37, true) }"
                            >
                                <template #title>
                                    <BaseSwitch
                                        v-model="isConfirmationFile"
                                        :label="$t('experience.step7.ticketingOptions.description_switch_2')"
                                    />
                                </template>
                                <template #content>
                                    <div class="form-section upload-container ppv2-w-full">
                                        <CustomInputUpload
                                            :label="$t('experience.step7.ticketingOptions.labelUpload')"
                                            v-model="confirmationFileEmail"
                                            :required="false"
                                            @input="onFileSelected"
                                            customClassLabel="poppinsMedium ppv2-text-black"
                                            classContainerAdd="ppv2-opacity-100"
                                            :customSizeLabel="14"
                                            :requiredTypeFileText="$t('experience.step7.ticketingOptions.requiredTypeFile')"
                                            accept="image/jpeg,image/png,image/webp,application/pdf"
                                            :maxSize="5 * 1024 * 1024"
                                        />
                                        <a
                                            v-if="stored_confirmaed_file && stored_confirmaed_file.toLowerCase().includes('.pdf')"
                                            :href="stored_confirmaed_file"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-mt-2 ppv2-text-blue-600 ppv2-underline"
                                        >
                                            <svg
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M14 2H6C4.89 2 4 2.89 4 4V20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                                                    fill="currentColor"
                                                />
                                                <path d="M9 13H15V15H9V13ZM9 17H15V19H9V17ZM9 9H12V11H9V9Z" fill="currentColor" />
                                            </svg>
                                            {{ $t('experience.step7.ticketingOptions.viewPdf') }}
                                        </a>
                                        <img v-else-if="stored_confirmaed_file" :src="stored_confirmaed_file" height="147px" />
                                    </div>
                                </template>
                            </DashedCard>
                        </div>

                        <!-- Tickets possibles -->
                        <TicketsPossibles
                            class="ppv2-w-full"
                            :isTicketPossibles.sync="isTicketPossibles"
                            :minTicket="min_ticket"
                            :maxTicket="max_ticket"
                            :listTicketPossibles.sync="list_ticket_possibles"
                            :errors="errors"
                            @focus="activeField = 'tickets'"
                        />
                    </div>
                </template>
            </DashedCard>
            <DashedCard
                :title="$t('experience.step7.questionCustomer.title')"
                :title-size="15"
                min-height="150px"
                :margin-bottom="0"
                :margin-top="40"
                custom-class=""
                custom-class-title=""
            >
                <template #content>
                    <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center">
                        <div
                            class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
                            :style="{ gap: normalizeSize(14, true) }"
                            v-if="res_quest_customer.length === 0"
                        >
                            <p class="!ppv2-mb-0 ppv2-text-black" style="font-size: 12px">
                                {{ $t('experience.step7.questionCustomer.emptyQuestion') }}
                            </p>
                            <BaseButton
                                id="button"
                                type="button"
                                variant="secondary"
                                @click="openQuestionPopup"
                                :customFontSize="12"
                            >
                                <Plus :size="12" />
                                <span>{{ $t('experience.step7.questionCustomer.addQuestion') }}</span>
                            </BaseButton>
                        </div>

                        <div
                            v-else
                            class="questions-list ppv2-w-full ppv2-flex ppv2-flex-col"
                            :style="{ gap: normalizeSize(16, true) }"
                        >
                            <!-- LISTE DES QUESTIONS -->
                            <div class="questions-items" :style="{ margin: normalizeSize(35, true) }">
                                <div
                                    v-for="(question, index) in res_quest_customer"
                                    :key="index"
                                    class="question-item ppv2-flex ppv2-items-center ppv2-justify-start ppv2-relative"
                                    draggable="true"
                                    @dragstart="onDragStart($event, index)"
                                    @dragover="onDragOver($event)"
                                    @drop="onDrop($event, index)"
                                    @dragenter="onDragEnter($event)"
                                    @dragleave="onDragLeave($event)"
                                    @dragend="onDragEnd($event)"
                                >
                                    <GripVertical
                                        :size="16"
                                        color="#9D96A8"
                                        class="ppv2-cursor-grab ppv2-absolute ppv2-left-[-25px]"
                                    />
                                    <!-- INDEX ET DRAG -->
                                    <div
                                        class="ppv2-relative ppv2-flex ppv2-items-center ppv2-w-full"
                                        :style="{ gap: normalizeSize(22), padding: normalizeSize(22) }"
                                        :class="{
                                            'ppv2-rounded-t-lg': index === 0,
                                            'ppv2-rounded-b-lg': index === res_quest_customer.length - 1,
                                            'ppv2-rounded-lg': res_quest_customer.length === 1,
                                        }"
                                    >
                                        <div
                                            class="ppv2-text-[#9D96A8] font-inter fw-bold"
                                            :style="{ fontSize: normalizeFontSize(20) }"
                                        >
                                            {{ (index + 1).toString().padStart(2, '0') }}
                                        </div>
                                        <div
                                            class="ppv2-flex-1"
                                            :style="{
                                                paddingLeft: normalizeSize(16, true),
                                                paddingRight: normalizeSize(16, true),
                                            }"
                                        >
                                            <span
                                                class="poppinsSemiBold ppv2-text-black uppercaseFirstLetter ppv2-text-normal"
                                                :style="{ fontSize: normalizeFontSize(13) }"
                                            >
                                                {{ question.name }}
                                            </span>
                                            <!-- TYPE DE RÉPONSE -->
                                            <div
                                                class="ppv2-flex ppv2-items-center poppinsRegular ppv2-text-black gap-1"
                                                :style="{ fontSize: normalizeFontSize(12) }"
                                            >
                                                <span v-for="(opt, optIndex) in question.options" :key="optIndex">
                                                    {{ opt.name }}<span v-if="optIndex < question.options.length - 1">, </span>
                                                </span>
                                            </div>
                                            <!-- OBLIGATOIRE -->
                                            <div class="ppv2-flex ppv2-items-center">
                                                <div
                                                    v-if="question.isRequired"
                                                    class="poppinsRegular ppv2-text-black"
                                                    style="font-size: 11px"
                                                >
                                                    <span> {{ $t('experience.step7.labels.required') }} </span>
                                                    <span>-</span>
                                                </div>
                                                <div class="poppinsRegular ppv2-text-black" style="font-size: 11px">
                                                    {{ getQuestionTypeLabel(question.type) }}
                                                </div>
                                            </div>
                                        </div>
                                        <!-- ACTIONS -->
                                        <div class="ppv2-flex ppv2-items-center actions" :style="{ gap: normalizeSize(10) }">
                                            <button @click="editQuestion(index)" class="ppv2-text-black">
                                                <SquarePen :size="16" />
                                            </button>
                                            <button @click="deleteQuestion(index)" class="ppv2-text-black">
                                                <Trash2 :size="16" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
                                :style="{ marginBottom: normalizeSizeVertical(40) }"
                            >
                                <BaseButton
                                    id="button_addQuestion"
                                    type="button"
                                    variant="secondary"
                                    @click="openQuestionPopup"
                                    :customFontSize="12"
                                >
                                    <Plus :size="12" />
                                    <span>{{ $t('experience.step7.questionCustomer.addQuestion') }}</span>
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </template>
            </DashedCard>
            <DashedCard
                :title="$t('experience.step7.specificMode.disponibilityTitle')"
                :title-size="15"
                min-height="150px"
                :margin-bottom="0"
                :margin-top="40"
                custom-class=""
                custom-class-title=""
            >
                <template #content>
                    <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center">
                        <div
                            class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
                            :style="{ gap: normalizeSize(14, true) }"
                            v-if="res_faq.length === 0"
                        >
                            <p class="!ppv2-mb-0 ppv2-text-black" style="font-size: 12px">
                                {{ $t('experience.step7.specificMode.emptyDisponibility') }}
                            </p>
                            <BaseButton
                                id="button"
                                type="button"
                                variant="secondary"
                                @click="popupVisible = true"
                                :customFontSize="12"
                            >
                                <Plus :size="12" />
                                <span>{{ $t('experience.step7.specificMode.addDisponibility') }}</span>
                            </BaseButton>
                        </div>

                        <div
                            v-else
                            class="faq-list ppv2-w-full ppv2-flex ppv2-flex-col"
                            :style="{ gap: normalizeSize(16, true) }"
                        >
                            <!-- LISTE DES INFOS PRATIQUES -->
                            <div class="faq-items" :style="{ margin: normalizeSize(35, true) }">
                                <div
                                    v-for="(faq, index) in res_faq"
                                    :key="index"
                                    class="faq-item question-item ppv2-flex ppv2-items-center ppv2-justify-start ppv2-relative"
                                    draggable="true"
                                    @dragstart="onDragStartFaq($event, index)"
                                    @dragover="onDragOver($event)"
                                    @drop="onDropFaq($event, index)"
                                    @dragenter="onDragEnter($event)"
                                    @dragleave="onDragLeave($event)"
                                    @dragend="onDragEnd($event)"
                                >
                                    <GripVertical
                                        :size="16"
                                        color="#9D96A8"
                                        class="ppv2-cursor-grab ppv2-absolute ppv2-left-[-25px]"
                                    />
                                    <!-- INDEX ET DRAG -->
                                    <div
                                        class="ppv2-relative ppv2-flex ppv2-items-center ppv2-w-full md:ppv2-p-5 ppv2-p-3"
                                        :style="{ gap: normalizeSize(22) }"
                                        :class="{
                                            'ppv2-rounded-t-lg': index === 0,
                                            'ppv2-rounded-b-lg': index === res_faq.length - 1,
                                            'ppv2-rounded-lg': res_faq.length === 1,
                                        }"
                                    >
                                        <div
                                            class="ppv2-text-[#9D96A8] font-inter fw-bold"
                                            :style="{ fontSize: normalizeFontSize(20) }"
                                        >
                                            {{ (index + 1).toString().padStart(2, '0') }}
                                        </div>
                                        <div
                                            class="ppv2-flex-1"
                                            :style="{
                                                paddingLeft: normalizeSize(16, true),
                                                paddingRight: normalizeSize(16, true),
                                            }"
                                        >
                                            <span
                                                class="poppinsSemiBold ppv2-text-black uppercaseFirstLetter ppv2-text-normal"
                                                :style="{ fontSize: normalizeFontSize(13) }"
                                            >
                                                {{ faq.name }}
                                            </span>
                                            <!-- DESCRIPTION -->
                                            <div
                                                class="ppv2-flex ppv2-items-center poppinsRegular ppv2-text-black gap-1 ppv2-text-normal"
                                                :style="{ fontSize: normalizeFontSize(12) }"
                                                v-html="faq.description"
                                            ></div>
                                        </div>
                                        <!-- ACTIONS -->
                                        <div class="ppv2-flex ppv2-items-center actions" :style="{ gap: normalizeSize(10) }">
                                            <button @click="editFaq(index)" class="ppv2-text-black">
                                                <SquarePen :size="16" />
                                            </button>
                                            <button @click="deleteFaq(index)" class="ppv2-text-black">
                                                <Trash2 :size="16" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div
                                class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
                                :style="{ marginBottom: normalizeSizeVertical(40) }"
                            >
                                <BaseButton
                                    id="button_addFaq"
                                    type="button"
                                    variant="secondary"
                                    @click="openFaqPopup"
                                    :customFontSize="12"
                                >
                                    <Plus :size="12" />
                                    <span>{{ $t('experience.step7.specificMode.addDisponibility') }}</span>
                                </BaseButton>
                            </div>
                        </div>
                    </div>
                </template>
            </DashedCard>

            <DashedCard
                :title="$t('experience.step7.displayOptions.title')"
                :title-size="15"
                custom-class=""
                custom-class-title=""
                :marginTop="40"
                :marginBottom="0"
                min-height="70px"
                :custom-style="{ padding: normalizeSize(24, true) }"
            >
                <template #content>
                    <div
                        class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-start"
                        :style="{ gap: normalizeSize(16, true) }"
                    >
                        <BaseSwitch
                            v-model="localShowAvailableSpots"
                            :label="$t('experience.step7.displayOptions.showAvailableSpots')"
                        />
                        <BaseSwitch
                            v-if="isBookingCircuit"
                            v-model="localShowBookEntierSpace"
                            :label="$t('experience.step7.displayOptions.showBookEntierSpace')"
                        />
                        <BaseSwitch
                            v-if="isBookingCircuit"
                            v-model="localShowDifficultyLevel"
                            :label="$t('experience.step7.displayOptions.showDifficultyLevel')"
                        />
                    </div>
                </template>
            </DashedCard>

            <DashedCard
                :title="$t('experience.step7.putOnline.title')"
                :title-size="15"
                custom-class=""
                custom-class-title=""
                :marginTop="40"
                :marginBottom="0"
                min-height="70px"
                :custom-style="{ padding: normalizeSize(24, true) }"
            >
                <template #content>
                    <div class="ppv2-flex ppv2-flex-col" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-center">
                            <BaseSwitch v-model="localVisibleOnline" :label="$t('experience.step7.putOnline.description')" />
                        </div>
                        <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-start ppv2-justify-center">
                            <BaseSwitch
                                v-model="localVisibleOnlinePos"
                                :label="$t('experience.step7.putOnline.visibilityPosLabel')"
                            />
                        </div>
                    </div>
                </template>
            </DashedCard>
            <!-- Stock Management Section (Static UI) -->
            <div v-if="stockManagmentEnabled">
                <div
                    v-if="!isStockManagementEnabled"
                    class="ppv2-relative"
                    :style="{ marginTop: normalizeSizeVertical(27, true), marginBottom: normalizeSizeVertical(20, true) }"
                >
                    <div class="ppv2-mx-[10px] ppv2-px-1 ppv2-bg-white dark:ppv2-bg-dark">
                        <BaseSwitch
                            v-model="isStockManagementEnabled"
                            :label="$t('experience.step7.stock_management.switch_label')"
                        />
                    </div>
                </div>
                <DashedCard
                    v-if="isStockManagementEnabled"
                    :marginTop="40"
                    :marginBottom="0"
                    :custom-style="{ padding: normalizeSize(24, true) }"
                >
                    <template #title>
                        <BaseSwitch
                            v-model="isStockManagementEnabled"
                            :label="$t('experience.step7.stock_management.switch_label')"
                        />
                    </template>
                    <template #content>
                        <div v-if="isStockManagementEnabled" class="ppv2-flex ppv2-flex-col ppv2-gap-6 ppv2-mt-4">
                            <!-- Produits consommés selector (like TicketsPossibles) -->
                            <BaseSelect
                                v-model="selectedProductIds"
                                :label="$t('experience.step7.stock_management.products_consumed')"
                                :multiple="true"
                                :options="cabinProducts"
                                option-label="name"
                                option-value="id"
                                :searchable="true"
                                :floatingLabel="true"
                                :floatingLabelBg="theme.mode === 'dark' ? '#141243' : '#fff'"
                                class="transparent-select ppv2-w-full"
                                class-added="!ppv2-h-[40px] !ppv2-min-h-[40px] !ppv2-text-xs ppv2-border-[#5B5B9C]"
                                :customSizeLabel="13"
                                :showSelectionControls="true"
                                @input="onProductMultiSelect"
                            />

                            <!-- Product List Sections -->
                            <div v-if="consumedProductsWithNames.length > 0" class="ppv2-flex ppv2-flex-col ppv2-gap-4">
                                <div
                                    v-for="(item, idx) in consumedProductsWithNames"
                                    :key="idx"
                                    class="ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-border ppv2-border-[#5B5B9C] ppv2-rounded-[12px] ppv2-flex ppv2-items-center ppv2-justify-between ppv2-gap-6"
                                    :style="{ padding: normalizeSize(22) }"
                                >
                                    <div class="ppv2-flex-1 ppv2-flex ppv2-items-center ppv2-gap-2">
                                        <span class="ppv2-text-black ppv2-text-sm">
                                            {{ item.name || 'Produit sans nom' }}
                                        </span>
                                    </div>

                                    <!-- Quantity Input with floating label -->
                                    <CustomInput
                                        v-model="consumedProducts[idx].quantity"
                                        type="number"
                                        :label="$t('experience.step7.stock_management.column_quantity')"
                                        :floatingLabel="true"
                                        :floatingLabelBg="theme.mode === 'dark' ? '#151933' : '#f2f2f2'"
                                        :min="1"
                                        :customSizeLabel="12"
                                        classInputAdd="!ppv2-min-h-[30px] !ppv2-h-[30px] !ppv2-rounded-lg "
                                        classContainerAdd="!ppv2-mb-0 !ppv2-w-[65px]"
                                    />

                                    <!-- Rule Segmented Control -->
                                    <SegmentedControl
                                        v-model="consumedProducts[idx].rule"
                                        :options="[
                                            {
                                                label: $t('experience.step7.stock_management.rule_per_reservation'),
                                                value: 'per_reservation',
                                            },
                                            {
                                                label: $t('experience.step7.stock_management.rule_per_person'),
                                                value: 'per_person',
                                            },
                                        ]"
                                        activeColor="#8B5CF6"
                                        :containerStyle="{ backgroundColor: 'rgba(0, 0, 0, 0.15)', borderRadius: '5px' }"
                                        customToggleOptionClass="!ppv2-rounded-[5px] !ppv2-p-1 !ppv2-text-xs"
                                    />

                                    <!-- Circular Delete Button -->
                                    <div
                                        class="ppv2-min-w-[26px] ppv2-min-h-[26px] ppv2-bg-black ppv2-bg-opacity-[5%] ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-justify-center ppv2-cursor-pointer hover:ppv2-bg-red-500/10 transition-all group"
                                        @click="removeConsumedProduct(idx)"
                                    >
                                        <X :size="12" class="ppv2-text-black group-hover:ppv2-text-red-400" />
                                    </div>
                                </div>
                            </div>

                            <!-- Info Banner -->
                            <InfoBanner
                                :text="$t('experience.step7.stock_management.description')"
                                variant="info"
                                :customFontSize="12"
                            />
                        </div>
                    </template>
                </DashedCard>
            </div>

            <div class="form-actions" :class="{ '!ppv2-justify-end': this.complited }">
                <BackButton v-show="!this.complited" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="submit" variant="primary" @click="submitForm" :loading="loading">
                    {{ !this.complited ? $t('experience.finish') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>
        <CustomDialog v-model="popupQuestionVisible" :defaultTitle="popupQuestionTitle" customClassTitle="poppinsSemiBold">
            <template #content>
                <div
                    v-for="(question, index) in questions"
                    :key="index"
                    class="faq-form ppv2-flex ppv2-flex-col mb-4"
                    :style="{ gap: normalizeSize(15) }"
                >
                    <DashedCard
                        custom-class-title="bgTitle ppv2-py-[1px] ppv2-text-black"
                        :title-size="15"
                        :margin-bottom="15"
                        :custom-style="{ padding: normalizeSize(40, true) }"
                    >
                        <template #title> {{ $t('experience.step7.questionCustomer.question') }} #{{ index + 1 }} </template>
                        <template #content>
                            <div class="space-y-4">
                                <CustomInput
                                    :label="$t('experience.step7.questionCustomer.labelNom')"
                                    :required="true"
                                    v-model="question.name"
                                    :placeholder="$t('experience.step7.nom')"
                                    :state="getState('name', index)"
                                    :errorText="errors[index]?.name"
                                    @input="clearError('name', index)"
                                    :customSizeLabel="14"
                                    customClassLabel="poppinsMedium ppv2-text-black"
                                    classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                                />

                                <div class="ppv2-flex ppv2-items-end" :style="{ gap: normalizeSize(43, true) }">
                                    <BaseSelect
                                        :label="$t('experience.step7.questionCustomer.type_reponse')"
                                        v-model="question.type"
                                        :options="typeOptionsQuestion"
                                        :searchable="true"
                                        :customSizeLabel="14"
                                        class="transparent-select ppv2-w-1/2"
                                        @input="onTypeChange(index, $event)"
                                        fixed-dropdown
                                    />
                                    <div class="ppv2-h-[40px] ppv2-flex ppv2-items-center">
                                        <BaseSwitch
                                            v-model="question.isRequired"
                                            :label="$t('experience.step7.labels.required')"
                                        />
                                    </div>
                                </div>
                                <div v-if="showOption(question.type)" class="ppv2-w-full">
                                    <label class="poppinsMedium ppv2-text-black" style="font-size: clamp(12px, 0.729545vw, 16px)">
                                        {{ $t('experience.step7.labels.options') }}
                                    </label>

                                    <!-- One input per option -->
                                    <div
                                        v-for="(opt, optIndex) in question.options"
                                        :key="optIndex"
                                        class="ppv2-flex ppv2-items-center mb-2"
                                        :style="{ gap: '10px' }"
                                    >
                                        <CustomInput v-model="opt.name" :label="$t('experience.step7.labels.option')" />

                                        <!-- Remove option button -->
                                        <div
                                            class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex align-items-center justify-center px-2"
                                            style="width: 30px"
                                        >
                                            <v-btn
                                                class="transparent-btn ppv2-text-black"
                                                depressed
                                                small
                                                outlined
                                                @click="removeOption(index, optIndex)"
                                            >
                                                <Minus :size="15" />
                                            </v-btn>
                                        </div>
                                        <!-- Add option button -->
                                        <div
                                            class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex align-items-center justify-center px-2"
                                            style="width: 30px"
                                        >
                                            <v-btn
                                                class="transparent-btn ppv2-text-black"
                                                depressed
                                                small
                                                outlined
                                                @click="addOption(index)"
                                            >
                                                <Plus :size="15" />
                                            </v-btn>
                                        </div>
                                    </div>
                                    <!-- Add first option button (visible when no options exist) -->
                                    <div
                                        v-if="!question.options || question.options.length === 0"
                                        class="ppv2-flex ppv2-items-center mb-2"
                                    >
                                        <v-btn
                                            class="transparent-btn ppv2-text-black"
                                            depressed
                                            small
                                            outlined
                                            @click="addOption(index)"
                                        >
                                            <Plus :size="15" />
                                            <span class="ml-1">{{ $t('experience.step7.labels.option') }}</span>
                                        </v-btn>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </DashedCard>
                </div>
                <!-- Centered "Add" button -->
                <div class="ppv2-flex ppv2-items-center ppv2-justify-center" :style="{ marginBottom: normalizeSizeVertical(40) }">
                    <BaseButton
                        @click="duplicateQuestionClient"
                        id="button"
                        type="button"
                        variant="secondary"
                        :customFontSize="12"
                    >
                        <Plus :size="12" />
                        <span>{{ $t('experience.step7.specificMode.addDisponibility') }}</span>
                    </BaseButton>
                </div>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="primary"
                    customClass="!ppv2-min-h-[40px]"
                    class="mt-4"
                    @click="submitQuestions"
                >
                    {{ $t('common.save') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <CustomDialog v-model="popupVisible" :defaultTitle="popupTitle" customClassTitle="poppinsSemiBold">
            <template #content>
                <DashedCard
                    :title="$t('experience.step7.specificMode.popupTitleSection')"
                    title-has-bg="bgTitle ppv2-py-[1px] ppv2-text-black"
                    :title-size="15"
                    :margin-bottom="45"
                    :custom-style="{ padding: normalizeSize(40, true) }"
                >
                    <template #content>
                        <div
                            v-for="(faq, index) in faqs"
                            :key="index"
                            class="faq-form ppv2-flex ppv2-flex-col mb-4"
                            :style="{ gap: normalizeSize(34) }"
                        >
                            <CustomInput
                                :label="$t('experience.step7.labelNom')"
                                :required="true"
                                v-model="faq.name"
                                :placeholder="$t('experience.step7.nom')"
                                :state="getState('name', index)"
                                :errorText="errors[index]?.name"
                                @input="clearError('name', index)"
                                :customSizeLabel="14"
                                customClassLabel="poppinsMedium ppv2-text-black"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0"
                            />

                            <BaseTextarea
                                :label="$t('experience.step7.labelDescription')"
                                v-model="faq.description"
                                :placeholder="$t('experience.step7.description')"
                                :useEditor="true"
                                :state="getState('description', index)"
                                :errorText="errors[index]?.description"
                                @input="clearError('description', index)"
                                :rows="3"
                                :customLabelStyle="`font-size: ${normalizeSize(14, true)}`"
                                customLabelClass="poppinsMedium ppv2-text-black"
                                classContainerAdd="!ppv2-opacity-100 !ppv2-mb-0 ppv2-gap-2"
                            />

                            <!-- Optional: remove a form -->
                            <div class="ppv2-flex ppv2-justify-end mt-2 ppv2-gap-2">
                                <div class="action-icon-btn" @click="removeForm(index)">
                                    <Trash2 :size="14" class="ppv2-text-red-500" />
                                </div>
                                <div class="action-icon-btn" @click="moveUp(index)">
                                    <MoveUp :size="14" class="ppv2-text-black" />
                                </div>
                                <div class="action-icon-btn" @click="moveDown(index)">
                                    <MoveDown :size="14" class="ppv2-text-black" />
                                </div>
                            </div>

                            <hr v-if="index < faqs.length - 1" class="ppv2-my-4 ppv2-border-gray-300" />
                        </div>

                        <!-- Centered "Add" button -->
                        <div
                            class="ppv2-flex ppv2-items-center ppv2-justify-center"
                            :style="{ marginBottom: normalizeSizeVertical(40) }"
                        >
                            <BaseButton @click="duplicate" id="button" type="button" variant="secondary" :customFontSize="12">
                                <Plus :size="12" />
                                <span>{{ $t('experience.step7.specificMode.addDisponibility') }}</span>
                            </BaseButton>
                        </div>
                    </template>
                </DashedCard>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="primary"
                    customClass="!ppv2-min-h-[40px]"
                    class="mt-4"
                    @click="submitAdditionalSale"
                >
                    {{ $t('common.save') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <CustomDialog v-model="showExitPopup" :defaultTitle="popupTitleExit" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    customClass="!ppv2-min-h-[40px]"
                    @click="showExitPopup = false"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="primary"
                    @click="showExitPopup = false"
                    :customHeight="43"
                    customClass="!ppv2-min-w-[250px] !ppv2-min-h-[40px]"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <!-- Delete Confirmation Popup -->
        <CustomDialog v-model="showDeletePopup" :defaultTitle="deletePopupTitle" customClassTitle="poppinsSemiBold" noScroll>
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ deletePopupDescription }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    id="button"
                    type="button"
                    variant="outline"
                    @click="showDeletePopup = false"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.cancel') }}
                </BaseButton>
                <BaseButton
                    type="submit"
                    variant="danger"
                    @click="confirmDelete"
                    :customClass="`!ppv2-min-h-[40px] !ppv2-text-base !ppv2-min-w-[170px]`"
                    :customHeight="45"
                >
                    {{ $t('common.delete') }}
                </BaseButton>
            </template>
        </CustomDialog>
        <PreviewExperience :experience="experience" :activeField="activeField" :step="step" />
    </div>
</template>
<script>
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import DashedCard from '../../../Shared/DashedCard.vue'
import CustomButtonSave from '../../../Shared/CustomButtonSave.vue'
import PreviewExperience from './PreviewExperience.vue'
import CustomDialog from '../../../Shared/CustomDialog.vue'
import CustomInput from '../../../Inputs/CustomInput.vue'
import BaseTextarea from '../../../Shared/BaseTextarea.vue'
import {
    Minus,
    Plus,
    Trash2,
    MoveUp,
    MoveDown,
    GripVertical,
    ChevronDown,
    SquarePen,
    Check,
    ChevronRight,
    Info,
    X,
} from 'lucide-vue'
import HeaderStep from './HeaderStep.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import BaseLabel from '../../../Shared/BaseLabel.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import CustomInputUpload from '../../../Inputs/CustomInputUpload.vue'
import InfoBanner from '../../../Shared/InfoBanner.vue'
import SegmentedControl from '../../../Shared/SegmentedControl.vue'
import TicketsPossibles from './TicketsPossibles.vue'
import { circuitDetectionService, CIRCUIT_TYPES, CONFIDENCE_LEVELS } from '../../../../services/CircuitDetectionService'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    components: {
        ChevronRight,
        BaseLabel,
        HeaderStep,
        BackButton,
        BaseButton,
        Plus,
        Minus,
        BaseSwitch,
        DashedCard,
        CustomButtonSave,
        PreviewExperience,
        CustomDialog,
        CustomInput,
        BaseTextarea,
        Trash2,
        MoveUp,
        MoveDown,
        BaseSelect,
        CustomInputUpload,
        GripVertical,
        ChevronDown,
        SquarePen,
        Check,
        TicketsPossibles,
        InfoBanner,
        SegmentedControl,
        Info,
        X,
        CustomDialog,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        selected_experience: { type: Object, required: false },
        prestation: { type: Object, required: false },
        id: { type: [Number, String], default: null },
        category: {
            type: Object,
            required: false,
        },
        sub_category: {
            type: Object,
            required: false,
        },
        complited: {
            type: Boolean,
            required: false,
        },
        products: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            stockManagmentEnabled: false,
            localPrestation: null,
            experience: {},
            activeField: null,
            popupVisible: false,
            popupTitle: this.$t('experience.step7.specificMode.popupTitle'),
            errors: {},
            faqs: [
                { name: '', description: '' }, // initial form
            ],
            res_faq: [],
            questions: [
                {
                    type: 'text',
                    name: '',
                    isRequired: false,
                    options: [{ name: '' }],
                },
            ],
            showExitPopup: false,
            popupTitleExit: this.$t('experience.titleClosePopup'),
            loading: false,
            initialFaq: null,
            localVisibleOnline: true,
            localVisibleOnlinePos: true,
            localVoucher: false,
            // Tickets state now handled by store
            isConfirmationFile: false,
            confirmationFileEmail: null,
            stored_confirmaed_file: '',
            res_quest_customer: [],
            popupQuestionVisible: false,
            popupQuestionTitle: this.$t('experience.step7.questionCustomer.titlePopupQuestion'),
            draggedFaqIndex: null,
            editingFaqIndex: null,
            localShowAvailableSpots: false,
            localShowDifficultyLevel: false,
            // Stock Management State
            isStockManagementEnabled: true,
            selectedProductIds: [1, 2],
            showDeletePopup: false,
            deletePopupTitle: '',
            deletePopupDescription: '',
            itemToDelete: null,
            deleteAction: null, // 'faq' or 'question'
            consumedProducts: [
                { id: 1, name: 'Produit 1', quantity: 1, rule: 'per_reservation' },
                { id: 2, name: 'Produit 2', quantity: 1, rule: 'per_reservation' },
            ],
            searchProduct: null,
            // Circuit Detection Service constants
            CIRCUIT_TYPES,
            CONFIDENCE_LEVELS,
            initialDataString: null,
            localShowBookEntierSpace: false,
        }
    },
    computed: {
        typeOptionsQuestion() {
            return [
                { value: 'text', label: this.$t('experience.step7.question_types.text') },
                { value: 'textarea', label: this.$t('experience.step7.question_types.textarea') },
                { value: 'select', label: this.$t('experience.step7.question_types.select') },
                { value: 'checkbox', label: this.$t('experience.step7.question_types.checkbox') },
                { value: 'multiple', label: this.$t('experience.step7.question_types.multiple') },
                { value: 'radio', label: this.$t('experience.step7.question_types.radio') },
            ]
        },
        stockRuleOptions() {
            return [
                { value: 'per_reservation', label: this.$t('experience.step7.stock_management.rule_per_reservation') },
                { value: 'per_person', label: this.$t('experience.step7.stock_management.rule_per_person') },
            ]
        },
        cabinProducts() {
            // For static UI phase, use mock data if products prop is empty
            if (!this.products || this.products.length === 0) {
                return [
                    { id: 1, name: 'Produit 1', is_cabin: 1 },
                    { id: 2, name: 'Produit 2', is_cabin: 1 },
                    { id: 3, name: 'Produit 3', is_cabin: 1 },
                ]
            }
            return (this.products || []).filter((p) => p.is_cabin == 1)
        },
        consumedProductsWithNames() {
            if (!this.consumedProducts) return []
            return this.consumedProducts.map((cp) => {
                // Determine ID (force to string for comparison)
                const cpId = cp.id ? String(cp.id) : ''
                const product = this.cabinProducts.find((p) => String(p.id) === cpId)

                return {
                    ...cp,
                    name: product ? product.name : cp.name || `Produit #${cpId}`,
                }
            })
        },
        isTicketPossibles: {
            get() {
                return this.$store.getters['experience/is_ticket_possibles']
            },
            set(val) {
                this.$store.dispatch('experience/setIsTicketPossibles', val)
            },
        },
        min_ticket: {
            get() {
                return this.$store.getters['experience/min_ticket']
            },
            set(val) {
                this.$store.dispatch('experience/setMinTicket', val)
            },
        },
        max_ticket: {
            get() {
                return this.$store.getters['experience/max_ticket']
            },
            set(val) {
                this.$store.dispatch('experience/setMaxTicket', val)
            },
        },
        list_ticket_possibles: {
            get() {
                return this.$store.getters['experience/list_ticket_possibles']
            },
            set(val) {
                this.$store.dispatch('experience/setListTicketPossibles', val)
            },
        },
        localSelectExperience() {
            return this.selected_experience || {}
        },
        hasId() {
            return this.id !== null && this.id !== undefined
        },
        matchingCircuits() {
            return this.$store.getters['experience/getMatchingCircuits']
        },
        isSimpleTicketCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'simpleTicket'
        },
        isBookingCircuit() {
            return this.matchingCircuits.length === 1 && this.matchingCircuits[0] === 'booking'
        },
        // 🔥 DÉTECTION AVEC CircuitDetectionService
        detectionInfo() {
            const userSelections = {
                reservation_mode: this.$store.getters['experience/reservationMode'],
                selectedOptionTarifMode: this.$store.getters['experience/selectedOptionTarifMode'],
                isActiveProhibitMixingMinPeople: this.$store.getters['experience/isActiveProhibitMixingMinPeople'],
                prohibitMixingMinPeople: this.$store.getters['experience/prohibitMixingMinPeople'],
            }

            const detection = circuitDetectionService.getBestDetection(userSelections)

            if (!detection.success) {
                return {
                    type: null,
                    score: 0,
                    confidence: CONFIDENCE_LEVELS.LOW,
                    isAmbiguous: false,
                }
            }

            return {
                type: detection.type,
                score: detection.score / 100,
                confidence: detection.confidence,
                isAmbiguous: detection.isAmbiguous,
                allScores: detection.allScores,
            }
        },
        detectedType() {
            const info = this.detectionInfo
            return info.confidence === CONFIDENCE_LEVELS.EXACT || info.confidence === CONFIDENCE_LEVELS.HIGH ? info.type : null
        },
        showDetectedType() {
            return this.$store.getters['experience/showDetectedType']
        },
        detectionMessage() {
            const info = this.detectionInfo
            if (!info.type) {
                return this.$t('experience.step3.detection.waitingConfig')
            }

            const typeName = circuitDetectionService.getTypeName(info.type)

            if (info.isAmbiguous) {
                return this.$t('experience.step3.detection.detectedTypeAmbiguous', { typeName })
            }

            return this.$t('experience.step3.detection.detectedType', { typeName })
        },
        hasDetectionAmbiguity() {
            return this.detectionInfo.isAmbiguous || false
        },
    },
    watch: {
        isBookingCircuit(val) {
            if (!val) {
                this.localShowDifficultyLevel = false
                this.localShowBookEntierSpace = false
            }
        },
        prestation: {
            handler(newVal) {
                if (newVal) {
                    this.localPrestation = newVal
                    if (
                        this.localPrestation.min_ticket !== undefined &&
                        this.localPrestation.min_ticket !== null &&
                        this.localPrestation.min_ticket !== 0
                    ) {
                        this.min_ticket = this.localPrestation.min_ticket
                    } else if (Number(this.localPrestation.type) > 0) {
                        this.min_ticket = Number(this.localPrestation.type)
                    } else {
                        // `type` vaut -1 par défaut en base : ne jamais recopier -1 dans le store partagé,
                        // le serveur exige un minimum >= 1 (même normalisation que Step5).
                        this.min_ticket = 1
                    }

                    if (this.localPrestation.max_ticket !== undefined && this.localPrestation.max_ticket !== null) {
                        this.max_ticket = this.localPrestation.max_ticket
                    } else if (
                        this.localPrestation.max_contributors !== undefined &&
                        this.localPrestation.max_contributors !== null &&
                        this.localPrestation.max_contributors !== 0
                    ) {
                        this.max_ticket = this.localPrestation.max_contributors
                    } else {
                        // Ensure it's -1 to show placeholder
                        this.max_ticket = -1
                    }
                    if (this.localPrestation.confirmation_file != null) {
                        this.isConfirmationFile = true
                        this.stored_confirmaed_file = this.localPrestation.confirmation_file
                        this.confirmationFileEmail = this.localPrestation.confirmation_file
                    }
                    if (this.localPrestation.list_numbers_tickes && this.localPrestation.list_numbers_tickes !== '') {
                        // Tolerant parsing: supports "1,2,3", "[1,2,3]" (legacy bug), or real JSON.
                        const raw = String(this.localPrestation.list_numbers_tickes).trim()
                        let parsed = []
                        if (raw.startsWith('[')) {
                            try {
                                const arr = JSON.parse(raw)
                                parsed = Array.isArray(arr) ? arr.map((v) => Number(v)) : []
                            } catch (_) {
                                parsed = raw
                                    .replace(/[[\]]/g, '')
                                    .split(',')
                                    .map((v) => Number(v.trim()))
                            }
                        } else {
                            parsed = raw.split(',').map((v) => Number(v.trim()))
                        }
                        this.list_ticket_possibles = parsed.filter((n) => Number.isFinite(n))
                    }
                    this.localVoucher = !this.isSimpleTicketCircuit && this.localPrestation.voucher == 1
                    this.localShowAvailableSpots = !!this.localPrestation.show_available_spots
                    this.localShowDifficultyLevel = !!this.localPrestation.show_difficulty_level
                    this.localShowBookEntierSpace = !!this.localPrestation.show_book_entier_space
                    // Le switch était figé à `true` : une expérience masquée depuis la liste
                    // rouvrait sa fiche avec la visibilité allumée.
                    // Comparaison explicite plutôt que !!Number() : la valeur arrive aujourd'hui
                    // de MySQL en 0/1, mais `!!"0"` vaut true et `Number("true")` vaut NaN — le
                    // jour où une API Resource la sérialise en booléen (ProductResource le fait
                    // déjà pour ce champ), le switch s'éteindrait tout seul. Champ absent ou nul
                    // (création) → visible par défaut.
                    this.localVisibleOnline = [undefined, null].includes(this.localPrestation.visible_online)
                        ? true
                        : [1, '1', true, 'true'].includes(this.localPrestation.visible_online)
                    this.localVisibleOnlinePos = [undefined, null].includes(this.localPrestation.visible_online_pos)
                        ? true
                        : [1, '1', true, 'true'].includes(this.localPrestation.visible_online_pos)
                    this.initialFaq = JSON.parse(JSON.stringify(this.res_faq))
                    if (this.localPrestation.faqs && this.localPrestation.faqs.length > 0) {
                        this.res_faq = this.localPrestation.faqs.map((f) => ({
                            name: f.faq,
                            description: f.content,
                        }))
                    }
                    if (this.localPrestation.questions && this.localPrestation.questions.length > 0) {
                        this.res_quest_customer = newVal.questions.map((f) => ({
                            id: f.id,
                            name: f.question,
                            type: f.type,
                            isRequired: f.required,
                            options: f.possibilities ? f.possibilities.map((o) => ({ name: o.possibility })) : [],
                        }))
                    }

                    // Load stock management from user_choices
                    const stockEnabled = this.getUserChoiceValue('stock_management_enabled')
                    this.isStockManagementEnabled = stockEnabled === '1' || stockEnabled === true

                    const stockData = this.getUserChoiceValue('consumed_products')
                    if (stockData) {
                        try {
                            this.consumedProducts = JSON.parse(stockData)
                            // Also sync selected IDs for the BaseSelect tags
                            this.selectedProductIds = this.consumedProducts.map((p) => p.id)
                        } catch (e) {
                            console.error('Error parsing stock data', e)
                            this.consumedProducts = []
                        }
                    }
                    this.initialDataString = this.getFormDataString()
                }
            },
            immediate: true,
            deep: true,
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        onProductMultiSelect(selectedIds) {
            if (!selectedIds) return
            // Add new products
            selectedIds.forEach((id) => {
                // Use loose equality to check existence
                if (!this.consumedProducts.find((cp) => cp.id == id)) {
                    const product = this.cabinProducts.find((p) => p.id == id)
                    if (product) {
                        this.consumedProducts.push({
                            id: product.id,
                            name: product.name,
                            quantity: 1,
                            rule: 'per_reservation',
                        })
                    }
                }
            })
            // Remove deselected products (use loose equality)
            this.consumedProducts = this.consumedProducts.filter((cp) => selectedIds.some((sid) => sid == cp.id))
        },
        onProductSelect(productId) {
            if (!productId) return
            const product = this.cabinProducts.find((p) => p.id === productId)
            if (product && !this.consumedProducts.find((cp) => cp.id === productId)) {
                this.consumedProducts.push({
                    id: product.id,
                    name: product.name,
                    quantity: 1,
                    rule: 'per_reservation',
                })
            }
            this.$nextTick(() => {
                this.searchProduct = null
            })
        },
        removeConsumedProduct(index) {
            this.consumedProducts.splice(index, 1)
        },
        getUserChoiceValue(key) {
            if (!this.localPrestation || !this.localPrestation.user_choices) return null
            const choice = this.localPrestation.user_choices.find((c) => c.key === key)
            return choice ? choice.value : null
        },
        duplicate() {
            // Add a new empty form
            this.faqs.push({ name: '', description: '' })
            this.errors.push({})
        },
        openQuestionPopup() {
            // Load existing questions into the form
            if (this.res_quest_customer.length > 0) {
                this.questions = JSON.parse(JSON.stringify(this.res_quest_customer))
            } else {
                this.questions = []
            }
            // Add a new empty question form
            this.questions.push({
                name: '',
                type: this.typeOptionsQuestion[0].value ?? this.typeOptionsQuestion[0],
                isRequired: false,
                options: [{ name: '' }],
            })
            this.popupQuestionVisible = true
        },
        duplicateQuestionClient() {
            this.questions.push({
                name: '',
                type: this.typeOptionsQuestion[0].value ?? this.typeOptionsQuestion[0],
                isRequired: false,
                options: [{ name: '' }],
            })
        },
        onTypeChange(index, newType) {
            const q = this.questions[index]
            if (this.showOption(newType)) {
                // Auto-initialize options when switching to an option-requiring type
                if (!q.options || q.options.length === 0) {
                    this.$set(q, 'options', [{ name: '' }])
                }
            }
        },
        addOption(index) {
            if (!this.questions[index].options) {
                this.$set(this.questions[index], 'options', [])
            }
            this.questions[index].options.push({ name: '' })
        },

        removeOption(questionIndex, optionIndex) {
            // remove option (works whether items are objects or strings, but now we always store objects)
            this.questions[questionIndex].options.splice(optionIndex, 1)
        },
        showOption(type) {
            return ['select', 'checkbox', 'multiple', 'radio'].includes(type)
        },
        removeForm(index) {
            this.faqs.splice(index, 1)
            this.errors.splice(index, 1)
        },
        removeFaq(index) {
            this.faqs.splice(index, 1)
            this.errors.splice(index, 1)
        },
        clearError(field, index) {
            if (this.errors[index]) {
                this.errors[index][field] = ''
            }
        },

        getState(field, index) {
            return this.errors[index]?.[field] ? 'error' : ''
        },

        submitAdditionalSale() {
            // Only save faqs that have a name filled in
            this.res_faq = this.faqs.filter((f) => f.name && f.name.trim() !== '')
            this.popupVisible = false
            this.editingFaqIndex = null
        },
        moveUp(index) {
            if (index > 0) {
                const temp = this.faqs[index - 1]
                this.$set(this.faqs, index - 1, this.faqs[index])
                this.$set(this.faqs, index, temp)

                // Move corresponding error state too (to keep them in sync)
                const tempErr = this.errors[index - 1]
                this.$set(this.errors, index - 1, this.errors[index])
                this.$set(this.errors, index, tempErr)
            }
        },

        moveDown(index) {
            if (index < this.faqs.length - 1) {
                const temp = this.faqs[index + 1]
                this.$set(this.faqs, index + 1, this.faqs[index])
                this.$set(this.faqs, index, temp)

                // Move corresponding error state too
                const tempErr = this.errors[index + 1]
                this.$set(this.errors, index + 1, this.errors[index])
                this.$set(this.errors, index, tempErr)
            }
        },
        openFaqPopup() {
            if (this.res_faq.length > 0) {
                this.faqs = JSON.parse(JSON.stringify(this.res_faq))
            } else {
                this.faqs = []
            }
            // Add a new empty faq form if it's not an edit
            if (!this.editingFaqIndex && this.editingFaqIndex !== 0) {
                this.faqs.push({ name: '', description: '' })
            }
            this.popupVisible = true
        },
        editFaq(index) {
            this.faqs = JSON.parse(JSON.stringify(this.res_faq))
            this.editingFaqIndex = index
            this.popupVisible = true
        },
        deleteFaq(index) {
            this.deleteAction = 'faq'
            this.itemToDelete = index
            this.deletePopupTitle = this.$t('experience.step7.specificMode.confirmDelete')
            this.deletePopupDescription = this.$t('experience.step7.specificMode.confirmDeleteText')
            this.showDeletePopup = true
        },
        onDragStartFaq(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this.draggedFaqIndex = index
        },
        onDropFaq(evt, targetIndex) {
            evt.preventDefault()
            const fromIndex = this.draggedFaqIndex
            const toIndex = targetIndex
            evt.currentTarget.style.backgroundColor = ''

            if (fromIndex !== toIndex && fromIndex !== undefined) {
                const item = this.res_faq.splice(fromIndex, 1)[0]
                this.res_faq.splice(toIndex, 0, item)
            }
        },
        onDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this.draggedIndex = index
        },

        onDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },

        onDrop(evt, targetIndex) {
            evt.preventDefault()
            const fromIndex = this.draggedIndex
            const toIndex = targetIndex
            evt.currentTarget.style.backgroundColor = ''

            if (fromIndex !== toIndex) {
                // Déplacer l'élément
                const item = this.res_quest_customer.splice(fromIndex, 1)[0]
                this.res_quest_customer.splice(toIndex, 0, item)
            }
        },

        onDragEnter(evt) {
            evt.currentTarget.style.backgroundColor = '#2C2C2CE5'
        },

        onDragLeave(evt) {
            if (!evt.currentTarget.contains(evt.relatedTarget)) {
                evt.currentTarget.style.backgroundColor = ''
            }
        },
        submitQuestions() {
            // Only save questions that have a name filled in
            this.res_quest_customer = this.questions.filter((q) => q.name && q.name.trim() !== '')
            this.popupQuestionVisible = false
        },
        async submitForm() {
            // Validation of min/max ticket is now handled in Step 5
            if (Object.keys(this.errors).length === 0) {
                const success = await this.saveExperience(false)
                if (success) {
                    this.$toast.success(this.$t('experience.saved'))
                    if (!this.complited) {
                        this.$emit('next-step')
                    }
                } else {
                    console.error('❌ Impossible de passer au step suivant - erreur de sauvegarde')
                }
            }
        },
        async saveAndExit() {
            this.$emit('save-and-exit')
        },
        async saveExperience(redirect) {
            this.loading = true
            this.error = null

            // Primary detected type uses exact matching (not scoring) for backend accuracy
            const primaryDetectedType = this.$store.getters['experience/getPrimaryDetectedType']
            const userSelections = this.$store.getters['experience/userSelections']

            const formData = new FormData()
            formData.append('step', this.step.id)
            formData.append('min_ticket', this.min_ticket)
            formData.append('max_ticket', this.max_ticket)
            formData.append('voucher', !this.isSimpleTicketCircuit && this.localVoucher ? 1 : 0)
            formData.append('isConfirmationFile', this.isConfirmationFile ? 1 : 0)
            formData.append('isTicketPossibles', this.isTicketPossibles ? 1 : 0)
            formData.append('visible_online', this.localVisibleOnline ? 1 : 0)
            formData.append('visible_online_pos', this.localVisibleOnlinePos ? 1 : 0)
            formData.append('show_available_spots', this.localShowAvailableSpots ? 1 : 0)
            formData.append('show_difficulty_level', this.isBookingCircuit && this.localShowDifficultyLevel ? 1 : 0)
            formData.append('show_book_entier_space', this.isBookingCircuit && this.localShowBookEntierSpace ? 1 : 0)
            formData.append('active', this.localPrestation?.active ? 1 : 0)
            formData.append('detected_type', primaryDetectedType || '')
            formData.append('detection_score', primaryDetectedType ? '1.0' : '')
            formData.append('detection_confidence', primaryDetectedType ? 'exact' : '')

            // Complex fields as JSON strings
            formData.append('res_faq', JSON.stringify(this.res_faq))
            formData.append('questions', JSON.stringify(this.res_quest_customer))
            // ✅ Send as real PHP array (list_ticket_possibles[]=1&list_ticket_possibles[]=2)
            // so the backend receives an array instead of a JSON string (which was being
            // stored literally as "[1,2,3]" in DB and produced NaNs on reload).
            {
                const list = Array.isArray(this.list_ticket_possibles) ? this.list_ticket_possibles : []
                if (list.length === 0) {
                    // Marker so PHP still sees the key as an (empty) array
                    formData.append('list_ticket_possibles[]', '')
                } else {
                    list.forEach((v) => formData.append('list_ticket_possibles[]', v))
                }
            }
            formData.append('user_selections', JSON.stringify(userSelections))
            formData.append('selectExperience', JSON.stringify(this.localSelectExperience))

            // Stock Management (Commented out for now as requested)
            // formData.append('stock_management_enabled', this.isStockManagementEnabled ? 1 : 0)
            // formData.append('consumed_products', JSON.stringify(this.consumedProducts))

            // File upload — only append if user selected a new file
            if (this.confirmationFileEmail instanceof File) {
                formData.append('confirmationFileEmail', this.confirmationFileEmail)
            }

            // Signal file removal: toggle is ON but user cleared the file
            if (this.isConfirmationFile && !this.confirmationFileEmail && !this.stored_confirmaed_file) {
                formData.append('removeConfirmationFile', 1)
            }

            if (this.localPrestation?.id) formData.append('id', this.localPrestation.id)

            try {
                const object = await axios.post('/api/v3/experience/save_exit_step_two', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                this.$emit('update-prestation', object.data.data)
                const baseUrl = `${window.location.protocol}//${window.location.host}`
                const experienceUrl = `${baseUrl}/back-office/v3/experience`
                if (redirect) {
                    window.location.href = experienceUrl
                    return
                }
                // Update initialDataString after saving
                this.initialDataString = this.getFormDataString()
                return true
            } catch (err) {
                console.error('❌ Erreur lors de la sauvegarde :', err.response || err)
                this.error = err.response?.data?.message || this.$t('experience.errors.save')
            } finally {
                this.loading = false
            }
            return false
        },
        exit() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
        getFormDataString() {
            return JSON.stringify({
                localVisibleOnline: this.localVisibleOnline,
                localVisibleOnlinePos: this.localVisibleOnlinePos,
                localVoucher: this.localVoucher,
                isConfirmationFile: this.isConfirmationFile,
                res_faq: this.res_faq,
                res_quest_customer: this.res_quest_customer,
                localShowAvailableSpots: this.localShowAvailableSpots,
                localShowDifficultyLevel: this.localShowDifficultyLevel,
                isStockManagementEnabled: this.isStockManagementEnabled,
                localShowBookEntierSpace: this.localShowBookEntierSpace,
                consumedProducts: this.consumedProducts,
                selectedProductIds: this.selectedProductIds,
                isTicketPossibles: this.isTicketPossibles,
                list_ticket_possibles: this.list_ticket_possibles,
            })
        },
        hasChanges() {
            if (!this.initialDataString) return false
            return this.initialDataString !== this.getFormDataString()
        },
        async handleExitClick() {
            if (this.hasChanges()) {
                this.showExitPopup = true
            } else {
                this.$emit('previous-step')
            }
        },
        onFileSelected(file) {
            if (!file) {
                this.confirmationFileEmail = null
                this.stored_confirmaed_file = ''
                return
            }
            this.confirmationFileEmail = file
        },
        getQuestionTypeLabel(type) {
            const typeMap = {
                text: this.$t('experience.step7.question_types.text'),
                textarea: this.$t('experience.step7.question_types.textarea'),
                select: this.$t('experience.step7.question_types.select'),
                checkbox: this.$t('experience.step7.question_types.checkbox'),
                multiple: this.$t('experience.step7.question_types.multiple'),
                radio: this.$t('experience.step7.question_types.radio'),
            }
            return typeMap[type] || type
        },

        getQuestionState(field, index) {
            return this.questionErrors[index]?.[field] ? 'error' : ''
        },

        getQuestionError(field, index) {
            return this.questionErrors[index]?.[field] || ''
        },

        clearQuestionError(field, index) {
            if (this.questionErrors[index]) {
                this.$delete(this.questionErrors[index], field)
            }
        },

        editQuestion(index) {
            this.questions = JSON.parse(JSON.stringify(this.res_quest_customer))
            this.editingQuestionIndex = index
            this.popupQuestionVisible = true
        },

        deleteQuestion(index) {
            this.deleteAction = 'question'
            this.itemToDelete = index
            this.deletePopupTitle = this.$t('experience.step7.questionCustomer.confirmDelete')
            this.deletePopupDescription = this.$t('experience.step7.questionCustomer.confirmDeleteText')
            this.showDeletePopup = true
        },

        confirmDelete() {
            if (this.deleteAction === 'faq') {
                this.res_faq.splice(this.itemToDelete, 1)
            } else if (this.deleteAction === 'question') {
                this.res_quest_customer.splice(this.itemToDelete, 1)
            }
            this.showDeletePopup = false
            this.itemToDelete = null
            this.deleteAction = null
        },

        moveQuestionUp(index) {
            if (index > 0) {
                const temp = this.questions[index - 1]
                this.$set(this.questions, index - 1, this.questions[index])
                this.$set(this.questions, index, temp)
            }
        },

        moveQuestionDown(index) {
            if (index < this.questions.length - 1) {
                const temp = this.questions[index + 1]
                this.$set(this.questions, index + 1, this.questions[index])
                this.$set(this.questions, index, temp)
            }
        },

        removeQuestionForm(index) {
            if (this.questions.length > 1) {
                this.questions.splice(index, 1)
            } else {
                // Réinitialiser la dernière question au lieu de la supprimer
                this.questions[0] = {
                    type: 'text',
                    name: '',
                    isRequired: false,
                    options: [{ name: '' }],
                }
            }
        },
        onDragEnd(evt) {
            // Nettoyer tous les styles de drag
            const allItems = document.querySelectorAll('.question-item')
            allItems.forEach((item) => {
                item.style.backgroundColor = ''
            })
            this.draggedIndex = null
        },
    },
}
</script>
<style scoped>
.form-section {
    flex: 1;
    /* max-width: 60%;*/
}

.bgTitle {
    background-color: var(--v-background-base) !important;
}
.dark .bgTitle {
    background-color: #141243 !important; /* Keep as is or use var if known */
}
.transparent-btn {
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    border: none;
    min-width: auto !important;
}
.transparent-btn:hover {
    background: #d9d9d91a !important;
}
.border_btns {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 8px;
    border: 1.5px solid #545595; /* Deep purple border */
    min-height: 30px;
    overflow: hidden;
    transition: all 0.2s ease;
}

.border_btns:hover {
    border-color: var(--ppv2-primary, #6c69ff);
    box-shadow: 0 2px 4px rgba(108, 105, 255, 0.1);
}

.dark .border_btns {
    border-color: #5667a1;
}

.dark .border_btns:hover {
    border-color: #7d7dff;
}

.action-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
    border-radius: 6px;
    border: 1.5px solid #545595;
    background: rgba(255, 255, 255, 0.05);
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 32px;
    min-height: 32px;
}

.action-icon-btn:hover {
    border-color: var(--ppv2-primary, #6c69ff);
    background: rgba(108, 105, 255, 0.1);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dark .action-icon-btn {
    border-color: #5667a1;
    background: rgba(255, 255, 255, 0.02);
}

.dark .action-icon-btn:hover {
    border-color: #7d7dff;
    background: rgba(125, 125, 255, 0.15);
}
.questions-list {
    background: transparent;
}

.question-item {
    transition: all 0.2s ease;
}

.question-item:hover {
    transform: translateY(-1px);
}

/* Style pour le drag and drop */
.ppv2-cursor-grab {
    cursor: grab;
}

.ppv2-cursor-grab:active {
    cursor: grabbing;
}

.actions {
    position: absolute;
    top: 6px;
    right: 8px;
    display: flex;
    gap: 10px;
    margin-top: 5px;
    opacity: 0;
    transition: opacity 0.2s ease;
}
.question-item:hover > svg.lucide-grip-vertical,
.question-item:hover > div {
    background-color: rgba(255, 255, 255, 0.1) !important;
}
.question-item svg.lucide-grip-vertical {
    opacity: 0;
}
.question-item:hover svg,
.question-item:hover .actions {
    opacity: 1;
}
</style>
